// frontend/src/components/resume/ResumeForm.tsx

import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { Button } from '@/components/common/UI/Button'
import { Input } from '@/components/common/UI/Input'
import { Select } from '@/components/common/UI/Select'
import { Card } from '@/components/common/UI/Card'
import { useToast } from '@/components/common/UI/Toast'

const resumeSchema = z.object({
  title: z.string().min(3, 'Название должно содержать минимум 3 символа'),
  position: z.string().min(2, 'Позиция обязательна'),
  specialization: z.string().optional(),
  experience_level: z.string().optional(),
  salary_from: z.number().min(0).optional().or(z.null()),
  salary_to: z.number().min(0).optional().or(z.null()),
  salary_currency: z.string().default('RUB'),
  show_salary: z.boolean().default(true),
  city: z.string().max(100).optional(),
  country: z.string().default('Россия'),
  relocation_ready: z.boolean().default(false),
  remote_ready: z.boolean().default(false),
  summary: z.string().max(5000).optional(),
  skills: z.array(z.string()).optional(),
  portfolio_links_text: z.string().optional().refine((value) => !value?.trim() || value.split(/[\n,]+/).every(url => { const text = url.trim(); if (!text) return true; try { const parsed = new URL(text); return ['http:', 'https:'].includes(parsed.protocol) } catch { return false } }), 'Укажите ссылки в формате https://example.com'),
  interests_text: z.string().optional(),
  recommendations_text: z.string().optional().refine((value) => !value?.trim() || value.split('\n').every(line => { const parts = line.split('|').map(part => part.trim()); return !parts.some(Boolean) || Boolean(parts[0]) }), 'Укажите имя рекомендателя в начале каждой строки'),
  is_public: z.boolean().default(true),
  is_searchable: z.boolean().default(true),
  is_anonymous: z.boolean().default(false),
})

type ResumeFormData = z.infer<typeof resumeSchema>

interface ResumeFormProps {
  resume?: any
  onSuccess?: () => void
  onCancel?: () => void
}

export const ResumeForm: React.FC<ResumeFormProps> = ({
  resume,
  onSuccess,
  onCancel
}) => {
  const { addToast } = useToast()
  const queryClient = useQueryClient()
  const [newSkill, setNewSkill] = useState('')
  const [resumeFile, setResumeFile] = useState<File | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch
  } = useForm<ResumeFormData>({
    resolver: zodResolver(resumeSchema),
    defaultValues: {
      title: resume?.title || '',
      position: resume?.position || '',
      specialization: resume?.specialization || '',
      experience_level: resume?.experience_level || '',
      salary_from: resume?.salary_from || null,
      salary_to: resume?.salary_to || null,
      salary_currency: resume?.salary_currency || 'RUB',
      show_salary: resume?.show_salary ?? true,
      city: resume?.city || '',
      country: resume?.country || 'Россия',
      relocation_ready: resume?.relocation_ready || false,
      remote_ready: resume?.remote_ready || false,
      summary: resume?.summary || '',
      skills: resume?.skills || [],
      portfolio_links_text: (resume?.portfolio_links || []).join('\n'),
      interests_text: (resume?.interests || []).join(', '),
      recommendations_text: (resume?.recommendations || []).map((item: any) => [item.name, item.position, item.contact, item.comment].filter(Boolean).join(' | ')).join('\n'),
      is_public: resume?.is_public ?? true,
      is_searchable: resume?.is_searchable ?? true,
      is_anonymous: resume?.is_anonymous ?? false,
    }
  })

  const watchSkills = watch('skills')

  const mutation = useMutation({
    mutationFn: async (data: ResumeFormData) => {
      const { portfolio_links_text, interests_text, recommendations_text, ...resumeFields } = data
      const payload = {
        ...resumeFields,
        salary_from: data.salary_from || null,
        salary_to: data.salary_to || null,
        skills: data.skills?.filter(s => s.trim()) || [],
        portfolio_links: portfolio_links_text?.split(/[\n,]+/).map(value => value.trim()).filter(Boolean) || [],
        interests: interests_text?.split(/[,\n]+/).map(value => value.trim()).filter(Boolean) || [],
        recommendations: recommendations_text?.split('\n').map(line => line.split('|').map(value => value.trim())).filter(parts => parts.some(Boolean) && parts[0]).map(([name, position, contact, comment]) => ({ name, position, contact, comment })),
      }

      if (resume) {
        return apiClient.put(`/v1/resumes/${resume.id}`, payload)
      }
      return apiClient.post('/v1/resumes', payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resumes'] })
      if (resume) {
        queryClient.invalidateQueries({ queryKey: ['resume', resume.id] })
      }
      addToast(
        resume ? 'Резюме обновлено' : 'Резюме создано',
        'success'
      )
      if (onSuccess) onSuccess()
    },
    onError: (error: any) => {
      addToast(
        error.response?.data?.detail || 'Произошла ошибка',
        'error'
      )
    }
  })

  const parseMutation = useMutation({
    mutationFn: (file: File) => apiClient.upload('/v1/resumes/parse', file),
    onSuccess: (parsed: any) => {
      if (parsed.title) setValue('title', parsed.title)
      if (parsed.position) setValue('position', parsed.position)
      if (parsed.summary) setValue('summary', parsed.summary)
      if (Array.isArray(parsed.skills)) setValue('skills', parsed.skills)
      addToast('Данные извлечены. Проверьте поля перед сохранением.', 'success')
    },
    onError: (error: any) => addToast(error.response?.data?.detail || 'Не удалось разобрать файл', 'error'),
  })

  const addSkill = () => {
    if (!newSkill.trim()) return
    const current = watchSkills || []
    if (current.includes(newSkill.trim())) {
      addToast('Этот навык уже добавлен', 'warning')
      return
    }
    setValue('skills', [...current, newSkill.trim()])
    setNewSkill('')
  }

  const removeSkill = (skill: string) => {
    const current = watchSkills || []
    setValue('skills', current.filter(s => s !== skill))
  }

  const onSubmit = (data: ResumeFormData) => {
    if (data.salary_from && data.salary_to && data.salary_from > data.salary_to) {
      addToast('Зарплата "до" должна быть больше зарплаты "от"', 'error')
      return
    }
    mutation.mutate(data)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Основная информация</h3>
        <div className="mb-5 flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg bg-secondary-50 p-4">
          <div className="flex-1">
            <p className="text-sm font-medium">Импортировать из PDF или Word</p>
            <p className="text-xs text-secondary-500">Мы извлечём текст и навыки; проверьте результат перед сохранением.</p>
          </div>
          <input type="file" accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={(event) => setResumeFile(event.target.files?.[0] || null)} className="text-sm" />
          <Button type="button" variant="outline" disabled={!resumeFile || parseMutation.isPending} onClick={() => resumeFile && parseMutation.mutate(resumeFile)}>
            {parseMutation.isPending ? 'Разбираем…' : 'Заполнить из файла'}
          </Button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Input
              label="Название резюме *"
              placeholder="Мое резюме"
              error={errors.title?.message}
              {...register('title')}
            />
          </div>

          <div className="md:col-span-2">
            <Input
              label="Позиция *"
              placeholder="Python разработчик"
              error={errors.position?.message}
              {...register('position')}
            />
          </div>

          <Input
            label="Специализация"
            placeholder="Backend разработка"
            error={errors.specialization?.message}
            {...register('specialization')}
          />

          <Select
            label="Уровень опыта"
            options={[
              { value: '', label: 'Не указан' },
              { value: 'no_experience', label: 'Без опыта' },
              { value: 'less_1_year', label: 'Менее 1 года' },
              { value: 'from_1_to_3', label: '1-3 года' },
              { value: 'from_3_to_6', label: '3-6 лет' },
              { value: 'more_6', label: 'Более 6 лет' },
            ]}
            error={errors.experience_level?.message}
            value={watch('experience_level') || ''}
            onChange={(value: string) => setValue('experience_level', value)}
          />
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Зарплата</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label="Зарплата от"
            type="number"
            placeholder="100000"
            error={errors.salary_from?.message}
            {...register('salary_from', { valueAsNumber: true })}
          />

          <Input
            label="Зарплата до"
            type="number"
            placeholder="200000"
            error={errors.salary_to?.message}
            {...register('salary_to', { valueAsNumber: true })}
          />

          <Select
            label="Валюта"
            options={[
              { value: 'RUB', label: '₽' },
              { value: 'USD', label: '$' },
              { value: 'EUR', label: '€' },
              { value: 'KZT', label: '₸' },
            ]}
            value={watch('salary_currency')}
            onChange={(value: string) => setValue('salary_currency', value)}
          />
        </div>

        <div className="mt-4">
          <label className="flex items-center gap-2 text-sm text-secondary-700">
            <input
              type="checkbox"
              checked={watch('show_salary')}
              onChange={(e) => setValue('show_salary', e.target.checked)}
              className="w-4 h-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
            />
            Показывать зарплату в резюме
          </label>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Местоположение</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Город"
            placeholder="Москва"
            error={errors.city?.message}
            {...register('city')}
          />

          <Input
            label="Страна"
            placeholder="Россия"
            error={errors.country?.message}
            {...register('country')}
          />

          <div>
            <label className="flex items-center gap-2 text-sm text-secondary-700">
              <input
                type="checkbox"
                checked={watch('remote_ready')}
                onChange={(e) => setValue('remote_ready', e.target.checked)}
                className="w-4 h-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
              />
              Готов к удаленной работе
            </label>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm text-secondary-700">
              <input
                type="checkbox"
                checked={watch('relocation_ready')}
                onChange={(e) => setValue('relocation_ready', e.target.checked)}
                className="w-4 h-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
              />
              Готов к переезду
            </label>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">О себе</h3>
        
        <label className="block text-sm font-medium text-secondary-700">
          Краткое описание
          <textarea rows={4} placeholder="Расскажите о себе..." className="mt-1.5 w-full rounded-md border border-secondary-300 px-3 py-2 text-secondary-900 placeholder:text-secondary-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20" {...register('summary')} />
          {errors.summary?.message && <span className="mt-1 block text-xs text-error">{errors.summary.message}</span>}
        </label>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Навыки</h3>
        
        <div className="flex gap-2 mb-3">
          <Input
            placeholder="Введите навык и нажмите Добавить"
            value={newSkill}
            onChange={(e) => setNewSkill(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
            className="flex-1"
          />
          <Button type="button" onClick={addSkill} className="flex-shrink-0">
            Добавить
          </Button>
        </div>

        <div className="flex flex-wrap gap-2">
          {watchSkills?.map((skill) => (
            <span
              key={skill}
              className="inline-flex items-center gap-1 px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-sm"
            >
              {skill}
              <button
                type="button"
                onClick={() => removeSkill(skill)}
                className="text-primary-400 hover:text-primary-600"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      </Card>

      <Card className="p-6 space-y-4">
        <h3 className="text-lg font-semibold">Портфолио, интересы и рекомендации</h3>
        <label className="block text-sm font-medium text-secondary-700">Ссылки на портфолио, проекты или публикации<textarea rows={3} placeholder="По одной ссылке на строку" aria-invalid={Boolean(errors.portfolio_links_text)} className="mt-1.5 w-full rounded-md border border-secondary-300 px-3 py-2" {...register('portfolio_links_text')} />{errors.portfolio_links_text?.message && <span className="mt-1 block text-xs text-error">{errors.portfolio_links_text.message}</span>}</label>
        <label className="block text-sm font-medium text-secondary-700">Профессиональные интересы<textarea rows={2} placeholder="Например: машинное обучение, наставничество, UX" className="mt-1.5 w-full rounded-md border border-secondary-300 px-3 py-2" {...register('interests_text')} /></label>
        <label className="block text-sm font-medium text-secondary-700">Рекомендации<textarea rows={4} placeholder="По одной рекомендации на строку: Имя | должность и компания | контакт | комментарий" aria-invalid={Boolean(errors.recommendations_text)} className="mt-1.5 w-full rounded-md border border-secondary-300 px-3 py-2" {...register('recommendations_text')} />{errors.recommendations_text?.message && <span className="mt-1 block text-xs text-error">{errors.recommendations_text.message}</span>}<span className="mt-1 block text-xs text-secondary-500">Указывайте только контакты, которые рекомендатель разрешил публиковать.</span></label>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-3">Видимость резюме</h3>
        <div className="space-y-3 text-sm text-secondary-700">
          <label className="flex items-start gap-2">
            <input type="checkbox" checked={watch('is_public')} onChange={(e) => setValue('is_public', e.target.checked)} className="mt-0.5" />
            <span>Разрешить просмотр резюме по прямой ссылке</span>
          </label>
          <label className="flex items-start gap-2">
            <input type="checkbox" checked={watch('is_searchable')} onChange={(e) => setValue('is_searchable', e.target.checked)} className="mt-0.5" />
            <span>Показывать резюме в поиске работодателей</span>
          </label>
          <label className="flex items-start gap-2">
            <input type="checkbox" checked={watch('is_anonymous')} onChange={(e) => setValue('is_anonymous', e.target.checked)} className="mt-0.5" />
            <span>Скрывать имя, фото и контакты до отклика работодателя</span>
          </label>
        </div>
      </Card>

      <div className="flex items-center justify-end gap-3 pt-4 border-t border-secondary-200">
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Отмена
          </Button>
        )}
        <Button type="submit" isLoading={isSubmitting || mutation.isPending}>
          {resume ? 'Сохранить' : 'Создать резюме'}
        </Button>
      </div>
    </form>
  )
}

export default ResumeForm
